import React from 'react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { SectionEmptyState } from '@/components/pages/AssetsPreviewPage/AssetsOverview/EmptyStates';
import { BasePieChart } from '@/modules';
import { SiteChartDataType } from '@/hooks';
import { generateTestId } from '@/utils/shared/generateTestIds';

interface HealthStatusesListProps {
  view: 'vertical' | 'horizontal';
  data: SiteChartDataType;
  siteName: string;
}

const HealthStatusesList = ({ view, data, siteName }: HealthStatusesListProps) => {
  return (
    <div className={cn('flex flex-1 gap-2', view === 'vertical' ? 'flex-col' : '')}>
      {data.map((el, index) => {
        const label = el.healthStatus === 'na' ? 'N/A' : el.healthStatus;
        return (
          <div
            key={el.healthStatus}
            className={cn(
              'flex items-center justify-between w-full gap-2',
              view === 'horizontal' ? 'flex-col max-w-40' : '',
              view === 'vertical' && index !== data.length - 1 ? 'border-b-[1px] pb-2' : '',
              view === 'horizontal' && index !== data.length - 1 ? 'border-r-[1px] pr-2' : ''
            )}
          >
            <div className="flex items-center gap-2">
              <div className={`h-2 w-1 ${el.color}`} />
              <div className="text-sm capitalize font-medium">{label}</div>
            </div>
            <div
              className="text-sm font-medium"
              data-testid={generateTestId(
                `assets_health_statuses_counts_${siteName}_${el.healthStatus}`
              )}
            >
              {el.value}
            </div>
          </div>
        );
      })}
    </div>
  );
};

interface AssetsCardProps {
  type: 'fixed' | 'mobile';
  totalAssets: number;
  chartData: SiteChartDataType;
  view: 'full' | 'half';
  handleClick: () => void;
  siteName: string;
}

export const AssetsCard = ({ totalAssets, type, chartData, view, handleClick, siteName }: AssetsCardProps) => {
  return (
    <>
      {totalAssets === 0 ? (
        <SectionEmptyState
          text={type === 'fixed' ? 'There are no fixed assets on this site' : 'There are no mobile fleet assets on this site'}
          className="bg-white h-52 border-0 flex items-center justify-center"
          dataTestId={generateTestId(`no_assets_${siteName}_${type}_assets`)}
        />
      ) : (
        <Card
          className="w-full h-52 border-0 flex-col justify-start gap-1 flex hover:ring-primary-600 hover:ring-2 hover:cursor-pointer shadow-sm"
          onClick={handleClick}
          data-testid={generateTestId(`${siteName}_${type}_assets`)}
        >
          <div className="flex items-center h-full w-full px-6 py-3 gap-4">
            <div className="font-semibold">{type === 'fixed' ? 'Fixed Assets' : 'Mobile Fleet Assets'}</div>
            <div className="h-full w-44 pointer-events-none">
              <BasePieChart
                chartData={{
                  data: chartData,
                  metaData: {
                    total: totalAssets,
                    totalTestId: generateTestId(`assets_health_statuses_counts_${siteName}_total`),
                  },
                }}
                hideTotalLabel
              />
            </div>
            <HealthStatusesList
              view={view === 'full' ? 'horizontal' : 'vertical'}
              data={chartData}
              siteName={siteName}
            />
          </div>
        </Card>
      )}
    </>
  );
};
